"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/app/_components/ui/carousel";
import { cn } from "@/app/_lib/utils";

const cloud = (id: string) =>
  `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${id}`;

export interface TeamMemberLite {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  photo: string | null;
  slug?: string | null;
}

// Fully-visible card count per breakpoint. At md/lg, card basis is 100/(N+1)
// so the leftover one card's worth of width splits into a half-card peek on
// each side when centered. On mobile that formula peeked too aggressively
// (crowding the 2 main cards), so its basis is a fixed 46% instead — just a
// small sliver of the neighbor shows. We still scroll N at a time either way.
function visibleCount(width: number) {
  if (width >= 1024) return 4; // lg
  if (width >= 768) return 3; // md
  return 2; // base/sm
}

/**
 * "Our people" carousel — shadcn/embla: N full cards centered with a
 * half-card peek on each side, scrolling N at a time. Arrows only appear on
 * hover and are vertically centered on the photo, not the caption below it.
 * Order is decided by the caller (TeamMember.order, isCore).
 */
export function TeamCarousel({ members }: { members: TeamMemberLite[] }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [selected, setSelected] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setSelected(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  React.useEffect(() => {
    if (!api) return;
    const applyForWidth = () => {
      const n = visibleCount(window.innerWidth);
      api.reInit({
        align: "center",
        loop: members.length > n,
        slidesToScroll: n,
      });
    };
    applyForWidth();
    window.addEventListener("resize", applyForWidth);
    return () => window.removeEventListener("resize", applyForWidth);
  }, [api, members.length]);

  if (!members.length) return null;

  return (
    <div className="group relative">
      <Carousel
        setApi={setApi}
        opts={{ align: "center", loop: members.length > 4 }}
        className="w-full "
      >
        <CarouselContent className="-ml-3 relative">
          {members.map((m) => (
            <CarouselItem
              key={m.id}
              className="basis-[46%] pl-3 md:basis-1/4 lg:basis-1/5"
            >
              {m.slug ? (
                <Link href={`/${m.slug}`} className="group/card block text-left">
                  <div className="relative aspect-[4/5] overflow-hidden bg-mint">
                    {m.photo && (
                      <Image
                        src={m.photo.startsWith("http") ? m.photo : cloud(m.photo)}
                        alt={`${m.firstName} ${m.lastName}`}
                        fill
                        sizes="(min-width: 1024px) 20vw, (min-width: 768px) 25vw, 46vw"
                        className="object-cover hover:scale-[1.03] duration-300 transition-transform duration-[2000ms]"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover/card:opacity-100">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                        See Portfolio <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                  <div className="pt-3">
                    <p className="text-[14px] font-semibold text-foreground">
                      {m.firstName} {m.lastName}
                    </p>
                    <p className="text-[13px] text-muted-foreground">
                      {m.position}
                    </p>
                  </div>
                </Link>
              ) : (
                <div className="text-left">
                  <div className="relative aspect-[4/5] overflow-hidden bg-mint">
                    {m.photo && (
                      <Image
                        src={m.photo.startsWith("http") ? m.photo : cloud(m.photo)}
                        alt={`${m.firstName} ${m.lastName}`}
                        fill
                        sizes="(min-width: 1024px) 20vw, (min-width: 768px) 25vw, 46vw"
                        className="object-cover hover:scale-[1.03] duration-300 transition-transform duration-[2000ms]"
                      />
                    )}
                  </div>
                  <div className="pt-3">
                    <p className="text-[14px] font-semibold text-foreground">
                      {m.firstName} {m.lastName}
                    </p>
                    <p className="text-[13px] text-muted-foreground">
                      {m.position}
                    </p>
                  </div>
                </div>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          className={cn(
            "hidden md:-left-6 lg:-left-8 top-0 bottom-[3.25rem] my-auto translate-y-0",
            "h-11 w-11 md:flex items-center justify-center rounded-full border border-border bg-white shadow-md transition-all duration-300 hover:border-primary hover:shadow-md active:scale-95",
            "opacity-0 group-hover:opacity-100",
          )}
        />
        <CarouselNext
          className={cn(
            "hidden md:-right-6 lg:-right-8 top-0 bottom-[3.25rem] my-auto translate-y-0",
            "h-11 w-11 md:flex items-center justify-center rounded-full border border-border bg-white shadow-md transition-all duration-300 hover:border-primary hover:shadow-md active:scale-95",
            "opacity-0 group-hover:opacity-100",
          )}
        />
      </Carousel>

      {count > 1 && (
        <div className="mt-8 flex justify-center gap-1.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={cn(
                "h-1 rounded-full transition-all",
                i === selected ? "w-7 bg-primary" : "w-5 bg-primary/20",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
